import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, User, Search, ChevronDown, LogOut } from 'lucide-react';
import DynamicLogo from './DynamicLogo';

const Header = ({ onMenuClick, isMobile }) => {
  const navigate = useNavigate();
  const [searchFocused, setSearchFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setShowDropdown(false);
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setShowNotifications(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.clear();
    setShowDropdown(false);
    navigate('/login', { replace: true });
  };

  const notifications = [
    { id: 1, title: 'New order received', time: '5 min ago', read: false },
    { id: 2, title: 'Product out of stock', time: '1 hour ago', read: false },
    { id: 3, title: 'Payment successful', time: '3 hours ago', read: true },
  ];

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <header className="header">
      <div className="header-left">
        <button className="btn btn-icon menu-btn" onClick={onMenuClick} aria-label="Toggle sidebar">
          <Menu size={24} />
        </button>

        {/* ✅ Dynamic Logo - Desktop only */}
        <div className="header-brand desktop-only">
          <DynamicLogo size="sm" />
        </div>

        <div className={`header-search ${searchFocused ? 'focused' : ''}`}>
          <Search size={18} />
          <input
            type="text"
            placeholder={isMobile ? 'Search...' : 'Search products, orders, customers...'}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          <kbd className="search-shortcut desktop-only">⌘K</kbd>
        </div>
      </div>

      <div className="header-right">
        <button className="btn btn-sm btn-primary desktop-only">
          <span>+ Add New</span>
        </button>

        <div className="header-dropdown" ref={notificationRef}>
          <button className="btn btn-icon notification-btn" onClick={() => setShowNotifications(!showNotifications)}>
            <Bell size={20} />
            {unreadCount > 0 && <span className="notification-dot">{unreadCount}</span>}
          </button>

          {showNotifications && (
            <div className="dropdown-menu notifications-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-title">Notifications</span>
                <button className="btn btn-sm btn-outline">Mark all read</button>
              </div>
              <div className="dropdown-divider" />
              <div className="notification-list">
                {notifications.map((notif) => (
                  <div key={notif.id} className={`notification-item ${!notif.read ? 'unread' : ''}`}>
                    <div className="notification-content">
                      <div className="notification-title">{notif.title}</div>
                      <div className="notification-time">{notif.time}</div>
                    </div>
                    {!notif.read && <div className="notification-unread-dot" />}
                  </div>
                ))}
              </div>
              <div className="dropdown-footer">
                <button className="btn btn-sm btn-outline w-full">View All Notifications</button>
              </div>
            </div>
          )}
        </div>

        <div className="header-dropdown" ref={dropdownRef}>
          <button className="btn btn-icon user-btn" onClick={() => setShowDropdown(!showDropdown)}>
            <User size={20} />
            <ChevronDown size={16} className="desktop-only" />
          </button>

          {showDropdown && (
            <div className="dropdown-menu user-dropdown-menu">
              <div className="dropdown-header">
                <div className="user-info">
                  <div className="user-avatar-large">A</div>
                  <div>
                    <div className="user-name">Admin</div>
                    <div className="user-email">admin@hayatherbal.com</div>
                  </div>
                </div>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item"><User size={16} /> Profile</button>
              <button className="dropdown-item" onClick={() => { setShowDropdown(false); navigate('/settings'); }}>
                <span>⚙️</span> Settings
              </button>
              <button className="dropdown-item" onClick={() => { setShowDropdown(false); navigate('/reports'); }}>
                <span>📊</span> Analytics
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item text-danger logout-btn" onClick={handleLogout}>
                <LogOut size={16} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;